import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { format } from 'date-fns'
import { reportService } from '@/services/reportService'

interface CohortData {
  cohort: string
  period: number
  users: number
  retention: number
  revenue: number
  transactions: number
}

const CohortAnalysis = () => {
  const [cohortStartDate, setCohortStartDate] = useState<Date | null>(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000))
  const [cohortEndDate, setCohortEndDate] = useState<Date | null>(new Date())
  const [cohortData, setCohortData] = useState<CohortData[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!cohortStartDate || !cohortEndDate) return

    let cancelled = false
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await reportService.fetchCohortAnalysis({
          startDate: format(cohortStartDate, 'yyyy-MM-dd'),
          endDate: format(cohortEndDate, 'yyyy-MM-dd'),
          groupBy: 'day',
        })
        if (!cancelled) setCohortData(res.items || [])
      } catch (e) {
        if (!cancelled) {
          setCohortData([])
          setError(e instanceof Error ? e.message : 'Failed to load cohort analysis')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [cohortStartDate, cohortEndDate])

  const cohortTable = useMemo(() => {
    const cohorts = Array.from(new Set(cohortData.map((d) => d.cohort)))
    const periods = Array.from(new Set(cohortData.map((d) => d.period))).sort((a, b) => a - b)

    return cohorts.map((cohort) => ({
      cohort,
      periods: periods.map((period) => {
        const data = cohortData.find((d) => d.cohort === cohort && d.period === period)
        return {
          period,
          retention: data?.retention || 0,
          users: data?.users || 0,
          revenue: data?.revenue || 0,
        }
      }),
    }))
  }, [cohortData])

  const retentionChartData = useMemo(() => {
    const cohorts = Array.from(new Set(cohortData.map((d) => d.cohort)))
    const periods = Array.from(new Set(cohortData.map((d) => d.period))).sort((a, b) => a - b)

    return periods.map((period) => {
      const dataPoint: any = { period: `Week ${period}` }
      cohorts.forEach((cohort) => {
        const data = cohortData.find((d) => d.cohort === cohort && d.period === period)
        dataPoint[cohort] = data?.retention || 0
      })
      return dataPoint
    })
  }, [cohortData])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      maximumFractionDigits: 0,
    }).format(value)
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Cohort Analysis
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Cohort Start Date"
                value={cohortStartDate}
                onChange={setCohortStartDate}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Cohort End Date"
                value={cohortEndDate}
                onChange={setCohortEndDate}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
          </Grid>
        </Paper>
      </LocalizationProvider>

      {loading && (
        <Paper sx={{ p: 3 }}>
          <Typography>Loading cohort data...</Typography>
        </Paper>
      )}

      {!loading && error && (
        <Paper sx={{ p: 3 }}>
          <Typography color="error.main">{error}</Typography>
        </Paper>
      )}

      {!loading && !error && cohortData.length === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">No cohort data in selected period.</Typography>
        </Paper>
      )}

      {!loading && cohortData.length > 0 && (
        <>
          {/* Retention Chart */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Retention Rate by Cohort
            </Typography>
            <Box sx={{ height: 400, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={retentionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis label={{ value: 'Retention %', angle: -90, position: 'insideLeft' }} />
                  <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
                  <Legend />
                  {Array.from(new Set(cohortData.map((d) => d.cohort))).map((cohort, index) => {
                    const colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042']
                    return (
                      <Bar key={cohort} dataKey={cohort} fill={colors[index % colors.length]} />
                    )
                  })}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Cohort Table */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Cohort Retention Table
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cohort</TableCell>
                    {Array.from(new Set(cohortData.map((d) => d.period)))
                      .sort((a, b) => a - b)
                      .map((period) => (
                        <TableCell key={period} align="right">
                          Week {period}
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cohortTable.map((row) => (
                    <TableRow key={row.cohort}>
                      <TableCell>
                        <Chip label={row.cohort} size="small" />
                      </TableCell>
                      {row.periods.map((periodData, index) => (
                        <TableCell key={index} align="right">
                          <Stack spacing={0.5} alignItems="flex-end">
                            <Typography variant="body2" fontWeight="medium">
                              {periodData.retention.toFixed(1)}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {periodData.users} users
                            </Typography>
                          </Stack>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Summary Cards */}
          <Grid container spacing={3}>
            {Array.from(new Set(cohortData.map((d) => d.cohort))).map((cohort) => {
              const cohortStats = cohortData.filter((d) => d.cohort === cohort)
              const totalUsers = cohortStats[0]?.users || 0
              const totalRevenue = cohortStats.reduce((sum, d) => sum + d.revenue, 0)
              const avgRetention = cohortStats.reduce((sum, d) => sum + d.retention, 0) / cohortStats.length

              return (
                <Grid item xs={12} sm={6} md={3} key={cohort}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {cohort}
                      </Typography>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Initial Users
                          </Typography>
                          <Typography variant="h6">{totalUsers}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Avg Retention
                          </Typography>
                          <Typography variant="h6">{avgRetention.toFixed(1)}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Total Revenue
                          </Typography>
                          <Typography variant="h6">{formatCurrency(totalRevenue)}</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </>
      )}
    </Box>
  )
}

export default CohortAnalysis


